import { Injectable } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { fromJS, List, Map } from 'immutable';
import { Observable } from 'rxjs';
import { v4 } from 'uuid';
import { compactSpaces } from '../../../../utils/helpers/string.helper';
import { ClientDataBuilder } from '../../../client-data/client-data.builder';
import { ClientDataService } from '../../../client-data/client-data.service';
import { Client } from '../../../client-data/client.model';
import { DocumentReferenceTypesEnum } from '../../../document-data/document-data.data';
import { Billing } from '../../../document-data/sub-models/billing.model';
import { Reference } from '../../../document-data/sub-models/reference.model';
import { Shipping } from '../../../document-data/sub-models/shipping.model';
import {
  IEntityData,
  ISearchParams,
  SortByEnum,
  SortOrderEnum,
} from '../../../entity-data/entity-data.data';
import { CONTENT_KEY } from '../../../entity-data/entity.model';
import { IFormMasterBuilder } from '../../../form-master/form-master.interface';
import { AddressData } from '../../../geolocation-data/models/address-data.model';
import { Settings } from '../../../settings-data/settings.model';
import { DocumentPresetSettings } from '../../../settings-data/sub-models/document-preset-settings/document-preset-settings.model';
import { IAutoCompleteOptions } from '../../../shared/directives/auto-complete/auto-complete.interface';
import {
  EMAIL_MAX_LENGTH,
  EmailService,
} from '../../../shared/services/email/email.service';
import { categoryTmpl, clientTmpl } from './auto-complete.tmpl';
import { map } from 'rxjs/operators';

export interface IClientAutoCompleteOptions {
  [formKey: string]: { autoComplete: IAutoCompleteOptions };
}

@Injectable()
export class AddClientBuilder implements IFormMasterBuilder {
  FORM_KEYS = {
    NAME: 'name',
    EMAIL: 'email',
    BILLING_ADDRESS: 'billingAddress',
    USE_SAME_BILLING_SHIPPING: 'useSameBillingShipping',
    SHIPPING_ADDRESS: 'shippingAddress',
    SAVE_TO_CLIENT_LIST: 'saveToClientList',
    TERMS: 'terms',
    PHONE: 'phone',
    MOBILE: 'mobile',
  };

  constructor(
    private _formBuilder: FormBuilder,
    private _translateService: TranslateService,
    private _clientDataBuilder: ClientDataBuilder,
    private _emailService: EmailService,
    private _clientDataService: ClientDataService
  ) {}

  buildForm(
    billing: Billing,
    shipping: Shipping,
    documentSettings: DocumentPresetSettings,
    enableMultipleEmails: boolean
  ): FormGroup {
    const formKeys = this.FORM_KEYS;
    const billingEmail = (billing && billing.email) || '';
    const emails = enableMultipleEmails
      ? this.buildEmailFormValue(billingEmail)
      : [billingEmail];
    const shippingAddress = shipping ? shipping.address : null;
    const billingAddress = billing ? billing.address : '';
    const mobile = billing ? billing.mobile : '';

    const formGroup = {
      [formKeys.NAME]: [
        billing ? billing.name : '',
        [Validators.required, Validators.maxLength(250)],
      ],
      [formKeys.EMAIL]: this._formBuilder.array(
        emails.map((email: string) => this.buildEmailFormControl(email)),
        this._emailService.emailArrayValidatorFn()
      ),
      [formKeys.BILLING_ADDRESS]: [billingAddress, Validators.maxLength(250)],
      [formKeys.TERMS]: [documentSettings.terms],
      [formKeys.USE_SAME_BILLING_SHIPPING]: [
        compactSpaces(billingAddress) === compactSpaces(shippingAddress),
      ],
      [formKeys.SHIPPING_ADDRESS]: [shippingAddress, Validators.maxLength(250)],
      [formKeys.PHONE]: [],
      [formKeys.MOBILE]: [mobile],
    };

    return this._formBuilder.group(formGroup);
  }

  buildEmailFormControl(email: string): FormControl {
    return this._formBuilder.control(email, [
      Validators.maxLength(EMAIL_MAX_LENGTH),
      this._emailService.emailValidatorFn(),
    ]);
  }

  buildEmailValue(emails: string[]): string {
    return this._emailService.concatenateEmails(emails);
  }

  buildEmailFormValue(email: string): string[] {
    return this._emailService.tokenizeEmails(email, ['']);
  }

  buildAutoCompleteOptions(): IClientAutoCompleteOptions {
    return {
      [this.FORM_KEYS.NAME]: this._buildAutoCompleteOptions(
        (query: string, client: Client): boolean =>
          (client.billingName || '')
            .toLowerCase()
            .indexOf(query.toLowerCase()) > -1,
        (client: Client) => client.billingName || ''
      ),
      [this.FORM_KEYS.EMAIL]: this._buildAutoCompleteOptions(
        (query: string, client: Client): boolean =>
          (client.email || '').toLowerCase().indexOf(query.toLowerCase()) > -1,
        (client: Client) => client.email || ''
      ),
      [this.FORM_KEYS.MOBILE]: this._buildAutoCompleteOptions(
        (query: string, client: Client): boolean =>
          (client.mobile || '').toLowerCase().indexOf(query.toLowerCase()) > -1,
        (client: Client) => client.mobile || ''
      ),
    };
  }

  buildEmptyAutoCompleteOptions(): IClientAutoCompleteOptions {
    return {
      [this.FORM_KEYS.NAME]: null,
      [this.FORM_KEYS.EMAIL]: null,
      [this.FORM_KEYS.MOBILE]: null,
    };
  }

  buildClient(
    billingInfo: Map<string, any>,
    shippingInfo: Map<string, any>,
    settings: Settings
  ): Client | null {
    const billingName: string = billingInfo.get(Billing.KEYS.NAME);

    if (billingName) {
      const clientProps = {
        [CONTENT_KEY]: {
          [Client.CONTENT_KEYS.BILLING_NAME]: billingInfo.get(
            Billing.KEYS.NAME
          ),
          [Client.CONTENT_KEYS.EMAIL]: billingInfo.get(Billing.KEYS.EMAIL),
          [Client.CONTENT_KEYS.MOBILE]: billingInfo.get(Billing.KEYS.MOBILE),
          [Client.CONTENT_KEYS.BILLING_ADDRESS]: billingInfo.get(
            Billing.KEYS.ADDRESS
          ),
        },
      };

      if (shippingInfo) {
        clientProps[Client.CONTENT_KEYS.SHIPPING_ADDRESS] = shippingInfo.get(
          Shipping.KEYS.ADDRESS
        );
      }

      return this._clientDataBuilder.buildEntity(clientProps, settings);
    } else {
      return null;
    }
  }

  buildBillingProps(
    billingInfo: Map<string, any>,
    billingAddressData: AddressData,
    reference: Reference
  ) {
    const formKeys = this.FORM_KEYS;
    const emails = billingInfo.get(formKeys.EMAIL) || List([]);
    const billingProps = {
      [Billing.KEYS.NAME]: billingInfo.get(formKeys.NAME),
      [Billing.KEYS.EMAIL]: this._emailService.concatenateEmails(
        emails.toArray()
      ),
      [Billing.KEYS.ADDRESS]: billingInfo.get(formKeys.BILLING_ADDRESS),
      [Billing.KEYS.ADDRESS_DATA]: billingAddressData,
      [Billing.KEYS.REFERENCE]: reference,
      [Billing.KEYS.PHONE]: billingInfo.get(formKeys.PHONE),
      [Billing.KEYS.MOBILE]: billingInfo.get(formKeys.MOBILE),
    };

    return fromJS(billingProps);
  }

  buildShippingProps(
    shippingInfo: Map<string, any>,
    shippingAddressData: AddressData
  ): Map<string, any> {
    if (shippingInfo) {
      const formKeys = this.FORM_KEYS;
      const useSameBillingShipping = shippingInfo.get(
        formKeys.USE_SAME_BILLING_SHIPPING
      );
      const billingAddress = shippingInfo.get(formKeys.BILLING_ADDRESS);
      const shippingAddress = shippingInfo.get(formKeys.SHIPPING_ADDRESS);
      const shippingProps = {
        [Shipping.KEYS.ADDRESS]: useSameBillingShipping
          ? billingAddress
          : shippingAddress,
        [Shipping.KEYS.ADDRESS_DATA]: shippingAddressData,
      };

      return fromJS(shippingProps);
    } else {
      return null;
    }
  }

  buildReference(client: Client): Reference {
    return new Reference({
      [Reference.KEYS.TYPE]: DocumentReferenceTypesEnum.client,
      [Reference.KEYS.ID]: client.id || v4(),
      [Reference.KEYS.REFERENCE_LOCAL_ID]: client.localId || v4(),
      [Reference.KEYS.REVISION_ID]: client.revisionId || v4(),
    });
  }

  private _buildAutoCompleteOptions(
    filterByFn: (query: string, client: Client) => boolean,
    setFn: (client: Client) => any
  ): { autoComplete: IAutoCompleteOptions } {
    const categoryLabel = this._translateService.instant(
      'LABEL.RECENTLY_CREATED_CLIENTS'
    );
    const limit = 20;
    const autoComplete: IAutoCompleteOptions = {
      async: true,
      asyncCallback: (query: string): Observable<Client[]> => {
        return this._clientDataService
          .searchEntities(this._buildSearchParams(query, limit))
          .pipe(
            map(({ entities }: IEntityData) =>
              entities[0]
                .filter((client: Client) => filterByFn(query, client))
                .toArray()
            )
          );
      },
      itemTemplate: clientTmpl,
      categoryTemplate: categoryTmpl.bind(null, categoryLabel),
      limit,
      setFn,
    };

    return { autoComplete };
  }

  private _buildSearchParams(query: string, limit: number): ISearchParams {
    return {
      query,
      page_size: limit,
      sort_order: SortOrderEnum.desc,
      sort_by: SortByEnum.created_date,
    };
  }
}
